import { space } from '@repo/theme/primitives';
import { getBezierEdgeCenter, Position } from '@xyflow/react';

const { 2: RUNWAY_LENGTH } = space;

interface GetLineageFlowBezierPathParams {
	sourceX: number;
	sourceY: number;
	sourcePosition?: Position;
	targetX: number;
	targetY: number;
	targetPosition?: Position;
	runwayLength?: number;
}

interface GetControlParams {
	pos: Position;
	x1: number;
	y1: number;
	x2: number;
	y2: number;
}

function getControl({
	pos,
	x1,
	y1,
	x2,
	y2,
}: GetControlParams): [number, number] {
	if (pos === Position.Left || pos === Position.Right) {
		return [0.5 * (x1 + x2), y1];
	}

	return [x1, 0.5 * (y1 + y2)];
}

/**
 * Returns a bezier path with horizontal runways at the start and end points
 * to make arrow markers look better.
 */
export function getLineageFlowBezierPath({
	sourceX,
	sourceY,
	sourcePosition = Position.Bottom,
	targetX,
	targetY,
	targetPosition = Position.Top,
	runwayLength = RUNWAY_LENGTH,
}: GetLineageFlowBezierPathParams): [
	path: string,
	labelX: number,
	labelY: number,
	offsetX: number,
	offsetY: number,
] {
	// Calculate horizontal runway points
	let startX = sourceX;
	let startY = sourceY;
	let endX = targetX;
	let endY = targetY;

	// Add horizontal runways based on position
	switch (sourcePosition) {
		case Position.Left:
			startX -= runwayLength;
			break;
		case Position.Right:
			startX += runwayLength;
			break;
		case Position.Top:
			startY -= runwayLength;
			break;
		case Position.Bottom:
			startY += runwayLength;
			break;
		default:
			break;
	}

	switch (targetPosition) {
		case Position.Left:
			endX -= runwayLength;
			break;
		case Position.Right:
			endX += runwayLength;
			break;
		case Position.Top:
			endY -= runwayLength;
			break;
		case Position.Bottom:
			endY += runwayLength;
			break;
		default:
			break;
	}

	// Calculate control points for the main curve
	const [sourceControlX, sourceControlY] = getControl({
		pos: sourcePosition,
		x1: startX,
		y1: startY,
		x2: endX,
		y2: endY,
	});

	const [targetControlX, targetControlY] = getControl({
		pos: targetPosition,
		x1: endX,
		y1: endY,
		x2: startX,
		y2: startY,
	});

	// Calculate label position
	const [labelX, labelY, offsetX, offsetY] = getBezierEdgeCenter({
		sourceX: startX,
		sourceY: startY,
		targetX: endX,
		targetY: endY,
		sourceControlX,
		sourceControlY,
		targetControlX,
		targetControlY,
	});

	// Create path with explicit horizontal segments
	const path = `M${sourceX},${sourceY} L${startX},${startY} C${sourceControlX},${sourceControlY} ${targetControlX},${targetControlY} ${endX},${endY} L${targetX},${targetY}`;

	return [path, labelX, labelY, offsetX, offsetY];
}
